(() => {
  const KEY = 'coffeeImmersive';

  class CoffeeImmersive {
    constructor(root) {
      if (root[KEY]) root[KEY].destroy();
      root[KEY] = this;
      this.root = root;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.form = root.querySelector('[data-product-form]');
      this.variants = this.readVariants();
      this.bind();
      this.updateVariant(false);
    }

    readVariants() {
      const node = this.root.querySelector('[data-variants-json]');
      if (!node) return [];
      try { return JSON.parse(node.textContent); } catch (_) { return []; }
    }

    bind() {
      this.root.addEventListener('change', (event) => {
        if (event.target.closest('[data-variant-picker]')) this.updateVariant(true);
      }, { signal: this.signal });
      this.root.addEventListener('click', (event) => {
        const minus = event.target.closest('[data-quantity-minus]');
        const plus = event.target.closest('[data-quantity-plus]');
        const sticky = event.target.closest('[data-sticky-add]');
        const videoToggle = event.target.closest('[data-video-toggle]');
        if (minus || plus) this.changeQuantity(plus ? 1 : -1);
        if (sticky) this.form?.requestSubmit();
        if (videoToggle) this.toggleVideo(videoToggle);
      }, { signal: this.signal });
      this.form?.addEventListener('submit', (event) => this.submit(event), { signal: this.signal });
      this.setupSticky();
    }

    selectedOptions() {
      return [...this.root.querySelectorAll('[data-variant-picker] fieldset')].map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        const select = fieldset.querySelector('select');
        return checked?.value || select?.value;
      });
    }

    updateVariant(updateUrl) {
      if (!this.variants.length) return;
      const options = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((value, index) => value === options[index]));
      if (!variant) return this.setUnavailable();
      this.currentVariant = variant;
      const id = this.root.querySelector('[data-variant-id]');
      if (id) id.value = variant.id;
      this.text('[data-price]', this.money(variant.price));
      this.text('[data-button-price]', this.money(variant.price));
      this.text('[data-sticky-price]', this.money(variant.price));
      const compare = this.root.querySelector('[data-compare-price]');
      if (compare) {
        compare.hidden = !(variant.compare_at_price > variant.price);
        compare.textContent = variant.compare_at_price ? this.money(variant.compare_at_price) : '';
      }
      this.setAvailability(variant.available);
      this.updateOptionAvailability(options);
      if (variant.featured_media?.id) this.swapMedia(variant.featured_media);
      if (updateUrl && this.root.dataset.updateUrl === 'true' && history.replaceState) {
        const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      }
      this.root.dispatchEvent(new CustomEvent('coffee:variant:change', { bubbles: true, detail: { variant } }));
    }

    updateOptionAvailability(selected) {
      this.root.querySelectorAll('[data-variant-picker] fieldset').forEach((fieldset, optionIndex) => {
        fieldset.querySelectorAll('input').forEach((input) => {
          const test = [...selected]; test[optionIndex] = input.value;
          const possible = this.variants.some((v) => v.available && v.options.every((value, index) => value === test[index]));
          input.disabled = !possible;
          if (!possible && fieldset.closest('[data-hide-unavailable]')?.dataset.hideUnavailable === 'true') input.nextElementSibling.hidden = true;
          else if (input.nextElementSibling) input.nextElementSibling.hidden = false;
        });
      });
    }

    setAvailability(available) {
      const button = this.root.querySelector('[data-add-to-cart]');
      const sticky = this.root.querySelector('[data-sticky-add]');
      if (button) button.disabled = !available;
      if (sticky) sticky.disabled = !available;
      this.text('[data-add-label]', available ? (button?.dataset.availableLabel || 'AJOUTER AU PANIER') : (button?.dataset.soldOutLabel || 'ÉPUISÉ'));
      const availability = this.root.querySelector('[data-availability]');
      if (availability) availability.dataset.available = String(available);
    }

    setUnavailable() { this.setAvailability(false); }

    changeQuantity(delta) {
      const input = this.root.querySelector('[data-quantity]');
      if (!input) return;
      const step = Number(input.step) || 1, min = Number(input.min) || 1, max = input.max ? Number(input.max) : Infinity;
      input.value = Math.min(max, Math.max(min, (Number(input.value) || min) + delta * step));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    money(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      const currency = window.Shopify?.currency?.active || 'EUR';
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency }).format(cents / 100);
    }

    async submit(event) {
      event.preventDefault();
      const button = this.root.querySelector('[data-add-to-cart]');
      const message = this.root.querySelector('[data-form-message]');
      if (!this.form || button?.disabled || button?.classList.contains('is-loading')) return;
      button?.classList.add('is-loading'); button?.setAttribute('aria-busy', 'true');
      if (message) message.textContent = '';
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(this.form) });
        const item = await response.json();
        if (!response.ok) throw new Error(item.description || item.message || 'Impossible d’ajouter ce produit.');
        if (message) message.textContent = 'Produit ajouté au panier.';
        this.root.dispatchEvent(new CustomEvent('cart:add', { bubbles: true, detail: { product: item, source: 'product-coffee-immersive' } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        this.refreshCartCount();
      } catch (error) {
        if (message) message.textContent = error.message;
      } finally {
        button?.classList.remove('is-loading'); button?.removeAttribute('aria-busy');
      }
    }

    async refreshCartCount() {
      try { const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json()); this.text('[data-cart-count]', cart.item_count); } catch (_) {}
    }

    swapMedia(media) {
      const image = this.root.querySelector('[data-media] img');
      const src = media.preview_image?.src || media.src;
      if (image && src) { image.src = `${src}${src.includes('?') ? '&' : '?'}width=2000`; image.removeAttribute('srcset'); }
    }

    toggleVideo(button) {
      const video = this.root.querySelector('[data-media] video');
      if (!video) return;
      if (video.paused) { video.play(); button.textContent = 'Ⅱ'; button.setAttribute('aria-label', 'Mettre la vidéo en pause'); }
      else { video.pause(); button.textContent = '▶'; button.setAttribute('aria-label', 'Lire la vidéo'); }
    }

    setupSticky() {
      const sticky = this.root.querySelector('[data-mobile-sticky]');
      if (!sticky) return;
      sticky.hidden = false;
      if (this.root.dataset.stickyTrigger === 'always' || !this.form || !('IntersectionObserver' in window)) return sticky.classList.add('is-visible');
      this.observer = new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 });
      this.observer.observe(this.form);
    }

    text(selector, value) { this.root.querySelectorAll(selector).forEach((node) => { node.textContent = value; }); }
    destroy() { this.controller.abort(); this.observer?.disconnect(); delete this.root[KEY]; }
  }

  const init = (scope = document) => scope.querySelectorAll('[data-coffee-section]').forEach((root) => new CoffeeImmersive(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-coffee-section]').forEach((root) => root[KEY]?.destroy()));
  document.addEventListener('shopify:block:select', (event) => event.target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' }));
})();
